import React, { useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, IconButton, Tooltip, Pagination, Stack, TableSortLabel, CircularProgress, Badge, TextField, InputAdornment } from '@mui/material';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import { LockClock, Mail, PsychologyAlt, Refresh, SearchOutlined, ErrorOutline } from '@mui/icons-material';
import { apiGet, apiPost, handleApiError, showErrorToast } from '../../../services/apiClient';
import { FilterKey, PotentialOffer, Tenant, TruckDetails } from '../../../types';
import { useTranslation } from 'react-i18next';
import ConsiderationsFilter from './ConsiderationsFilter';
import { Considerations, defaultConsiderations } from '../../../models/considerations';
import { PotentialOffersFilter } from '../../../models/potentialOffersFilter';
import { AutoSearchState, SearchFilterMode, VehicleAutoSearch } from '../../../models/vehicleAutoSearch';
import { toast } from '../../../utils/toast';
import { PotentialOffersOrderBy } from '../../../pages/loadSearch/loadSearch.page';
import NegotiationDialog, { NegotiationParams } from '../../dialogs/negotiations/negotiationDialog';
import ContinueNegotiationDialog from '../../dialogs/negotiations/continueNegotiationDialog';
import { fetchVehicles } from '../../../services/vehicleService';
import { parseAutoSearchError } from '../../../services/autoSessionService';
import { useNegotiationHandlers } from '../../../pages/loadSearch/components/useNegotiationHandlers';
import { useTheme } from '@mui/material/styles';
import OfferActionButton from './OfferActionButton';
import { NegotiationManualCancelReason } from '../../../models/negotiation';
import { resolveNegotiationForOffer, resolveNegotiationIdForOffer } from '../../../utils/negotiationOfferUtils';
import { TableSkeletonLoader } from '../../loaders';
import { useSubscriptionDialog } from '../../../context/subscriptionDialogContext';
import { EmptyState } from '../../emptyState/emptyState';
import { OfferRenderer } from '../../../renderers/OfferRenderer';
import { PushNotificationType } from '../../../models/user';
import { theme as appTheme } from '../../../theme/theme';
import { triggerNegotiationAlertsRefresh } from '../../../context/negotiationAlertsContext';
import useConfirm from '../../../hooks/ui/useConfirm';

const STALE_NEGOTIATION_DAYS = 14;

/**
 * Props for PotentialMatchesTable - now a presentational component
 * All fetching is done at the page level
 */
interface PotentialMatchesTableProps {
  mode?: 'all' | 'ongoing'; // used for differentiating between all offers and ongoing offers
  title: string;
  // Data
  selectedTruck: TruckDetails | null;
  offers: PotentialOffer[] | null;
  tenantData: Tenant | null;
  totalOffers: number;
  stats?: {
    unreadEmails: number;
    needHuman: number;
    waitingForClient: number;
  };
  // Loading state
  vehicleOffersLoading: boolean;
  isInitialLoad?: boolean; // When true, shows skeleton; when false, only shows spinner in header
  /** True when the last offers fetch failed (network/server error). Shows error empty state instead of generic "no results". */
  loadError?: boolean;
  // Pagination (controlled by parent)
  page: number;
  pageSize?: number;
  onPageChange: (page: number) => void;
  // Sorting (controlled by parent)
  orderBy: PotentialOffersOrderBy;
  order: 'ASC' | 'DESC' | undefined;
  onSortChange: (orderBy: PotentialOffersOrderBy) => void;
  // Filters
  considerations?: Considerations;
  filterMode?: SearchFilterMode;
  autoSearchSession?: VehicleAutoSearch | null;
  useAutoSearchSync?: boolean;
  selectedCountries?: string[];
  filters?: PotentialOffersFilter;
  /** Filter section keys to hide in ConsiderationsFilter. 'autoSearchSync' is automatically added when mode is 'ongoing'. */
  hiddenFilters?: Set<FilterKey>;
  offerPattern?: string | undefined;
  onConsiderationsChange?: (considerations: Considerations) => void;
  onFilterModeChange?: (mode: SearchFilterMode) => void;
  onUseAutoSearchSyncChange?: (sync: boolean) => void;
  onSelectedCountriesChange?: (countries: string[]) => void;
  onFiltersChange?: (filters: PotentialOffersFilter) => void;
  onUpdateAutoSearch?: () => Promise<void>;
  onOfferPatternChange?: (offerPattern: string | undefined) => void;
  // Refresh
  refreshCountdown: number;
  onRefresh: () => void;
  /** Optional page-level height override for the outer table card. */
  containerHeight?: string;
  /** Optional page-level max-height override for the scrollable table container. */
  tableContainerMaxHeight?: string;
  /** Ongoing table: optimistic remove + stale refetch filter (loadSearch) */
  onNegotiationClosed?: (offer: PotentialOffer) => void;
  onNegotiationCloseFailed?: (offer: PotentialOffer) => void;
  onNegotiationStarted?: (offer: PotentialOffer, negotiationId: string) => void;
  groupageBannerLabel?: string;
  onGroupageBannerClick?: () => void;
  onCreateGroupageTrip?: (offer: PotentialOffer) => void;
  onAddToGroupageTrip?: (offer: PotentialOffer) => void;
  groupagePendingOfferId?: number | null;
  groupageTripNegotiationIds?: Set<string>;
  groupageRemainingWeightT?: number;
  groupageRemainingLengthM?: number;
  currentGroupageTripId?: number | null;
  negotiationToGroupageTripMap?: Map<string, number>;
  onOpenOtherGroupageTrip?: (tripId: number) => void;
  /** Optional auto-search session info for the selected truck. Drives loading/empty/error UI distinctions. */
  autoSearchInfo?: AutoSearchInfo | null;
  /** Called when a push notification indicates the auto-search session state changed (completed or errored). */
  onAutoSearchUpdate?: () => void;
  confirmStaleOnOpen?: boolean;
}

export interface AutoSearchInfo {
  state: AutoSearchState;
  statusMessage?: string;
  /** True once at least one marketplace search execution has completed (regardless of result). */
  hasAnyExecution: boolean;
  /** Sum of marketplaceOffersFound across the last execution of every SearchRecord — i.e. raw offers found before client filters. */
  marketplaceOffersFound: number;
}

/**
 * Presentational component for displaying potential matches/offers.
 * All data fetching is handled by the parent page component.
 */
const PotentialMatchesTable: React.FC<PotentialMatchesTableProps> = ({
  mode = 'all',
  title,
  selectedTruck,
  offers,
  tenantData,
  totalOffers,
  stats,
  vehicleOffersLoading,
  isInitialLoad = true,
  loadError = false,
  page,
  pageSize = 15,
  orderBy,
  order,
  considerations = defaultConsiderations,
  filterMode = 'default',
  autoSearchSession,
  useAutoSearchSync = false,
  selectedCountries = [],
  filters,
  hiddenFilters,
  offerPattern,
  refreshCountdown,
  onPageChange,
  onSortChange,
  onConsiderationsChange,
  onFilterModeChange,
  onUseAutoSearchSyncChange,
  onSelectedCountriesChange,
  onFiltersChange,
  onUpdateAutoSearch,
  onOfferPatternChange,
  onRefresh,
  containerHeight,
  tableContainerMaxHeight,
  onNegotiationClosed,
  onNegotiationCloseFailed,
  onNegotiationStarted,
  groupageBannerLabel,
  onGroupageBannerClick,
  onCreateGroupageTrip,
  onAddToGroupageTrip,
  groupagePendingOfferId = null,
  groupageTripNegotiationIds,
  groupageRemainingWeightT,
  groupageRemainingLengthM,
  currentGroupageTripId,
  negotiationToGroupageTripMap,
  onOpenOtherGroupageTrip,
  autoSearchInfo = null,
  onAutoSearchUpdate,
  confirmStaleOnOpen = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { confirm, setConfirmIsOpen, setConfirmTitle, setConfirmDescription, setConfirmText, ConfirmModal } = useConfirm();

  // ===== Local UI State (dialogs, notifications) =====
  const [selectedOffer, setSelectedOffer] = useState<PotentialOffer | null>(null);
  const [negotiationModalOpen, setNegotiationModalOpen] = useState<boolean>(false);
  const [startNegotiation, setStartNegotiation] = useState<boolean>(false);
  const lastClickedOfferIdRef = useRef<number | null>(null);
  const { showSubscriptionDialog } = useSubscriptionDialog();

  const tableContainerRef = useRef<HTMLDivElement | null>(null);

  const effectiveHiddenFilters = useMemo<Set<FilterKey>>(() => new Set<FilterKey>(hiddenFilters), [hiddenFilters]);

  // Listen for push notifications from service worker to auto-refresh data
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const refreshTriggerTypes: PushNotificationType[] = [
      PushNotificationType.AUTO_SEARCH_COMPLETED,
      PushNotificationType.AUTO_SEARCH_ERRORED,
      PushNotificationType.NEGOTIATION_VALIDATION_REQUIRED,
      PushNotificationType.NEGOTIATION_EMAIL_RECEIVED,
      PushNotificationType.NEGOTIATION_HUMAN_REQUIRED
    ];
    const handleMessage = (event: MessageEvent) => {
      const messageType = event.data?.type;
      if (refreshTriggerTypes.includes(messageType)) {
        if (messageType === PushNotificationType.AUTO_SEARCH_COMPLETED && event.data?.data?.body && !event.data?.silent) {
          toast.info(event.data.data.body);
        }
        onRefresh();
        // Refresh auto-search session state on completion or error
        if (messageType === PushNotificationType.AUTO_SEARCH_COMPLETED || messageType === PushNotificationType.AUTO_SEARCH_ERRORED) {
          onAutoSearchUpdate?.();
        }
      }
    };
    navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
    };
  }, [onRefresh, onAutoSearchUpdate]);

  const [continueNegotiationModal, setContinueNegotiationModal] = useState<{
    open: boolean;
    vehicleId: string;
    negotiationId: string | undefined;
    messageId: string;
    messagesHistory: string[];
  }>({
    open: false,
    vehicleId: '',
    negotiationId: undefined,
    messageId: '',
    messagesHistory: [],
  });

  const {
    handleStartNegotiation,
    handleOrderConfirmNegotiation,
    handleAcceptNegotiation,
    handleAbortNegotiation,
    handleFetchNegotiation,
    setNegotiation,
    negotiation,
    ConfirmNegotiationModal } = useNegotiationHandlers();

  // ===== Column Definitions =====
  const cols: { id: string; label: string; sortable?: boolean, hide?: boolean }[] = [
    { id: 'id', label: t('ID') },
    { id: 'route', label: t('Route') },
    { id: 'latestStart', label: t('Timeline'), sortable: true },
    { id: 'estimatedPrice', label: t('Price'), sortable: true },
    { id: 'finalScore', label: t('Score'), sortable: true },
    { id: 'info', label: t('Info') },
    { id: 'contact', label: t('Contact') },
    { id: 'distance', label: t('Distance') },
    { id: 'weight_length', label: t('W/L') },
    { id: 'status', label: t('Status'), hide: mode === 'all' },
    { id: 'creationDateTime', label: t('Age'), sortable: true, hide: mode === 'ongoing' },
    { id: 'actions', label: t('Actions'), hide: mode === 'all' },
  ];

  // this one is used to get the updated offer after clicking on confirm, validate and other actions.
  useEffect(() => {
    if (selectedOffer && offers) {
      const offer = offers.find((offer) => offer.id === selectedOffer.id);
      if (offer) setSelectedOffer(offer);
    }
  }, [offers]);

  const handleRowClick = async (offer: PotentialOffer) => {
    if (!selectedTruck || !offer.id) return;
    lastClickedOfferIdRef.current = offer.id;
    setSelectedOffer(offer);

    // For potential matches (mode='all'), open negotiation dialog to start negotiation
    if (mode === 'all') {
      setNegotiationModalOpen(true);
      return;
    }

    // For ongoing offers (mode='ongoing'), fetch existing negotiation
    const negotiationId = offer.negotiations?.[0]?.id;
    if (!negotiationId) return;
    try {
      await handleFetchNegotiation(selectedTruck.id, negotiationId);

      if (confirmStaleOnOpen && offer.latestStart && dayjs(offer.latestStart).isBefore(dayjs().subtract(STALE_NEGOTIATION_DAYS, 'day'))) {
        setConfirmTitle(t('Old negotiation'));
        setConfirmDescription(t("This offer's loading date passed more than 2 weeks ago. Sending new messages may be ineffective. Open anyway?"));
        setConfirmText('Open anyway');
        const ok = await confirm();
        // useConfirm keeps the modal open after confirm() (for async work + spinner); close explicitly.
        setConfirmIsOpen(false);
        if (!ok) return;
      }

      setNegotiationModalOpen(true);
      // Viewing a negotiation marks its emails as read on the backend, so
      // nudge the sidebar badge to re-count without waiting for the poll.
      triggerNegotiationAlertsRefresh();
    } catch (err) {
      console.error('handleGetNegotiation error:', err);
      showErrorToast(err, t('Failed to fetch negotiation info'));
    }
  };

  /**
     * Closes the continue negotiation modal
     */
  const handleCloseContinueNegotiationModal = (): void => {
    setContinueNegotiationModal({
      open: false,
      vehicleId: '',
      negotiationId: undefined,
      messageId: '',
      messagesHistory: [],
    });
  };

  /**
     * Handles successful continue negotiation
     */
  const handleContinueNegotiationSuccess = (): void => {
    // Refresh the table after successful continue negotiation
    // fetchVehicleOffers(false);
  };

  const handleOpenNegotiationModal = (offer: PotentialOffer): void => {
    if (!selectedTruck) return;
    setSelectedOffer(offer);
    setNegotiationModalOpen(true);
  };

  const handleCloseNegotiationModal = (): void => {
    setSelectedOffer(null);
    setNegotiation(null);
    setNegotiationModalOpen(false);
  };

  useEffect(() => {
    if (!negotiationModalOpen) {
      setSelectedOffer(null);
      setNegotiation(null);
    }
  }, [negotiationModalOpen]);

  const handleSendMessage = async (message: string, subject?: string): Promise<void> => {
    if (!selectedOffer || !selectedTruck || !negotiation) return;

    setStartNegotiation(true);
    try {
      // if human reviewed AI generated message
      const aiBody = negotiation?.emailBodies?.find((body) => ['AI', 'MANAGER'].includes(body.sender) && !body.sent);
      if (aiBody) {
        await apiPost(`${process.env.REACT_APP_API_URL}/api/v1/vehicle/${selectedTruck.id}/negotiation/${negotiation.id}/progress`, {
          email_response: message
        });
      } else {
        await apiPost(`${process.env.REACT_APP_API_URL}/api/v1/vehicle/${selectedTruck.id}/negotiation/${negotiation.id}/add`, {
          email_response: message,
          email_subject: subject,
        });
      }

      setSelectedOffer(null);
      setNegotiationModalOpen(false);

      await fetchVehicles();
      onRefresh();
      triggerNegotiationAlertsRefresh();

      toast.success(t('Message sent successfully'));
    } catch (err: any) {
      handleApiError(err, t('Failed to send message'));
    } finally {
      setStartNegotiation(false);
    }
  };

  const handleStart = async (offerId: number, negotiationFormData: NegotiationParams) => {
    if (!selectedTruck) return;
    setStartNegotiation(true);

    try {

      const negotiationData = await handleStartNegotiation(selectedTruck.id, offerId, negotiationFormData);
      onRefresh();
      triggerNegotiationAlertsRefresh();
      if(selectedOffer) onNegotiationStarted?.(selectedOffer, negotiationData.negotiationId);

      if (negotiationFormData.autoSendInitial) {
        setNegotiationModalOpen(false);
        return;
      }

      await handleFetchNegotiation(selectedTruck.id, negotiationData?.negotiationId);
      setNegotiationModalOpen(true);

      if (negotiationFormData.localNego) {
        setContinueNegotiationModal({
          open: true,
          vehicleId: selectedTruck.id,
          negotiationId: negotiationData?.negotiationId,
          messageId: negotiationData?.messageId,
          messagesHistory: negotiationData?.messagesHistory,
        });
      }
    } catch (err: any) {
      if (err?.name === 'SubscriptionRequiredError') {
        showSubscriptionDialog(err.subscriptionType);
        return;
      }
      showErrorToast(err, t('Failed to start negotiation!'));
    } finally {
      setStartNegotiation(false);
    }
  };

  const handleCloseNegotiation = async (offer: PotentialOffer) => {
    if (!selectedTruck) return;

    try {
      onNegotiationClosed?.(offer);

      let negotiation = resolveNegotiationForOffer(offer, undefined);
      let negotiationId = resolveNegotiationIdForOffer(offer, negotiation);

      if (negotiationId === undefined) {
        const vehicleResponse = await apiGet(`${process.env.REACT_APP_API_URL}/api/v1/vehicle/${selectedTruck.id}`);
        const vehicleData = await vehicleResponse.json();
        negotiation = resolveNegotiationForOffer(offer, vehicleData.result.negotiations);
        negotiationId = resolveNegotiationIdForOffer(offer, negotiation);
      }

      if (negotiationId === undefined) {
        onNegotiationCloseFailed?.(offer);
        showErrorToast(undefined, t('Negotiation not found'));
        return;
      }

      await apiPost(
        `${process.env.REACT_APP_API_URL}/api/v1/vehicle/${selectedTruck.id}/negotiation/${negotiationId}/cancel`,
        { send_email: false, cancel_reason: NegotiationManualCancelReason.NOT_DISCLOSED }
      );

      toast.success(t('Negotiation closed successfully'));
      onRefresh();
      triggerNegotiationAlertsRefresh();
    } catch (err: any) {
      onNegotiationCloseFailed?.(offer);
      handleApiError(err, t('Failed to close negotiation'));
    }
  };

  // Sort Handler: delegates to parent
  const handleSortClick = (newOrderBy: PotentialOffersOrderBy) => {
    onSortChange(newOrderBy);
  };

  const hasNoData = !offers || offers.length === 0;
  // Empty current slice can mean either "no results at all" or "page out of range"; only the former triggers auto-search copies.
  const hasZeroTotal = !totalOffers;

  const isLocalLoading = vehicleOffersLoading && (isInitialLoad || hasNoData);
  const autoSearchInProgress = autoSearchInfo?.state === AutoSearchState.ON
    && !autoSearchInfo.hasAnyExecution
    && hasNoData;
  const showSkeleton = isLocalLoading || autoSearchInProgress;

  const showAutoSearchError = !showSkeleton && hasNoData && hasZeroTotal
    && autoSearchInfo?.state === AutoSearchState.ERRORED;
  const showLoadError = loadError && !showSkeleton;
  const showFilteredOutHint = !showSkeleton && !showAutoSearchError && !showLoadError && hasNoData && hasZeroTotal
    && autoSearchInfo?.state === AutoSearchState.ON
    && autoSearchInfo.hasAnyExecution
    && autoSearchInfo.marketplaceOffersFound > 0;
  const showMarketplaceEmpty = !showSkeleton && !showAutoSearchError && !showLoadError && !showFilteredOutHint && hasNoData && hasZeroTotal
    && autoSearchInfo?.state === AutoSearchState.ON
    && autoSearchInfo.hasAnyExecution
    && autoSearchInfo.marketplaceOffersFound === 0;
  const showGenericEmpty = !showSkeleton
    && !showLoadError
    && hasNoData
    && !showAutoSearchError
    && !showFilteredOutHint
    && !showMarketplaceEmpty;

  // Whether to show error chip in header (even when offers exist)
  const showErrorChip = autoSearchInfo?.state === AutoSearchState.ERRORED && !showSkeleton;

  return (
    <Box sx={{
      height: containerHeight ?? 'calc(75vh - 3.5rem)',
      bgcolor: hasNoData ? 'transparent' : undefined,
      borderRadius: { xs: 2, sm: 2.5 },
      border: (theme: any) => theme.palette.mode === 'dark'
        ? '1px solid rgba(255, 255, 255, 0.08)'
        : '1px solid rgba(0, 0, 0, 0.08)',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <Box sx={styles.tableHeader}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={styles.tableTitle}>{title}</Typography>
          {selectedTruck && totalOffers > 0 && (
            <Chip
              label={`${totalOffers}`}
              size="small"
              sx={{
                height: 22,
                borderRadius: 999,
                fontWeight: 700,
                bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.18)' : 'rgba(76, 175, 80, 0.14)'),
                color: (t) => (t.palette.mode === 'dark' ? '#9BE7B0' : '#1B5E20'),
                border: '1px solid',
                borderColor: (t) => (t.palette.mode === 'dark' ? 'rgba(76,175,80,0.28)' : 'rgba(76,175,80,0.22)'),
              }}
            />
          )}
          {showErrorChip && (
            <Tooltip title={parseAutoSearchError(autoSearchInfo?.statusMessage, t)} arrow>
              <Chip
                icon={<ErrorOutline sx={{ fontSize: 14 }} />}
                label={t('Search error')}
                size="small"
                sx={{
                  height: 22,
                  borderRadius: 999,
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.15)' : 'rgba(244, 67, 54, 0.08)'),
                  color: (t) => (t.palette.mode === 'dark' ? '#EF9A9A' : '#C62828'),
                  border: '1px solid',
                  borderColor: (t) => (t.palette.mode === 'dark' ? 'rgba(244,67,54,0.3)' : 'rgba(244,67,54,0.2)'),
                  '& .MuiChip-icon': { color: 'inherit' },
                }}
              />
            </Tooltip>
          )}
          {mode === 'ongoing' && (
            <Table sx={{ width: 'auto', display: 'table' }}>
              <TableRow sx={{
                '&:last-child td': {
                  borderBottom: 'none',
                },
              }}>
                <TableCell>
                  <Tooltip title={t('Unread Emails')}>
                    <Badge badgeContent={stats?.unreadEmails} color="error">
                      <Mail color="action" />
                    </Badge>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip title={t('Requires Attention')}>
                    <Badge badgeContent={stats?.needHuman} color="warning">
                      <PsychologyAlt color="action" />
                    </Badge>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip title={t('Waiting for client’s response')}>
                    <Badge badgeContent={stats?.waitingForClient} color="primary">
                      <LockClock color="action" />
                    </Badge>
                  </Tooltip>
                </TableCell>
              </TableRow>
            </Table>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {groupageBannerLabel && (
            <Chip
              icon={<MergeTypeIcon sx={{fontSize: 18}} />}
              label={groupageBannerLabel}
              color="info"
              variant="outlined"
              onClick={onGroupageBannerClick}
              sx={{fontSize: '0.8rem', height: 30, borderRadius: 1, cursor: onGroupageBannerClick ? 'pointer' : 'default'}}
            />
          )}
          {mode !== 'ongoing' && (
            <Tooltip title={t('Search in offers cities, countries and contacts')}>
              <TextField
                value={offerPattern || ''}
                onChange={(e) => onOfferPatternChange && onOfferPatternChange(e.target.value)}
                placeholder={t('Search offers')}
                aria-label={t('Search offers')}
                size="small"
                variant="outlined"
                hiddenLabel
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ mr: 0, ml: 0.25 }}>
                      <SearchOutlined sx={{ fontSize: 17, color: 'text.secondary', opacity: 0.85 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: { xs: 140, sm: 168 },
                  '& .MuiOutlinedInput-root': {
                    minHeight: 38,
                    height: 38,
                    borderRadius: '10px',
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    bgcolor: (theme) => (theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.04)'
                      : 'rgba(15,23,42,0.03)'),
                    transition: 'border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
                    '& fieldset': {
                      borderColor: (theme) => (theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.12)'
                        : 'rgba(15,23,42,0.1)'),
                    },
                    '&:hover fieldset': {
                      borderColor: (theme) => (theme.palette.mode === 'dark'
                        ? 'rgba(167,139,250,0.45)'
                        : 'rgba(109,40,217,0.35)'),
                    },
                    '&.Mui-focused': {
                      bgcolor: (theme) => (theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.06)'
                        : '#fff'),
                      boxShadow: (theme) => (theme.palette.mode === 'dark'
                        ? '0 0 0 2px rgba(139,92,246,0.25)'
                        : '0 0 0 2px rgba(109,40,217,0.12)'),
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: 1,
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    px: 0.25,
                    boxSizing: 'border-box',
                    '&::placeholder': {
                      color: 'text.secondary',
                      opacity: 0.65,
                      fontWeight: 400,
                    },
                  },
                }}
              />
            </Tooltip>
          )}
          {onConsiderationsChange && (
            <>
              <ConsiderationsFilter
                considerations={considerations}
                filterMode={filterMode}
                autoSearchSession={autoSearchSession}
                useAutoSearchSync={useAutoSearchSync}
                selectedCountries={selectedCountries}
                filters={filters}
                onConsiderationsChange={onConsiderationsChange}
                onFilterModeChange={onFilterModeChange}
                onUseAutoSearchSyncChange={onUseAutoSearchSyncChange}
                onSelectedCountriesChange={onSelectedCountriesChange}
                onFiltersChange={onFiltersChange}
                onUpdateAutoSearch={mode === 'all' ? onUpdateAutoSearch : undefined}
                hiddenFilters={effectiveHiddenFilters}
              />
              {mode === 'all' && (
                <Tooltip title={
                  filterMode === 'default' ? t('Default - No destination filter') :
                    filterMode === 'preferredRoute' ? t('Circle Trip - Assigned route') :
                      filterMode === 'preferredDestinations' ? t('Preferred Destinations - Configured destinations') :
                        filterMode === 'returnDestinations' ? t('Return Destinations - Back to registration country') :
                          filterMode === 'selectedCountries' ? t('Selected Countries - Specific countries only') :
                            t('Default')
                }>
                  <Chip
                    label={(() => {
                      const modeLabels: Record<string, string> = {
                        default: t('Any'),
                        preferredRoute: t('Circle'),
                        preferredDestinations: t('Dest'),
                        returnDestinations: t('Return'),
                        selectedCountries: t('Countries'),
                      };
                      return modeLabels[filterMode] || t('Any');
                    })()}
                    size="small"
                    variant="outlined"
                    sx={{
                      fontSize: '0.65rem',
                      height: 20,
                      minWidth: 'auto',
                      '& .MuiChip-label': { px: 0.75 },
                      borderColor: filterMode === 'default' ? 'text.grey' : 'primary.main',
                      color: filterMode === 'default' ? 'text.grey' : 'primary.main',
                    }}
                  />
                </Tooltip>
              )}
            </>
          )}
          {selectedTruck && (
            <>
              {vehicleOffersLoading && (
                <CircularProgress size={14} thickness={4} sx={{ color: 'primary.main' }} />
              )}
              <Typography variant="body2" sx={{ color: 'text.grey', fontSize: (theme: any) => theme.typography.fontSizes.small, fontVariantNumeric: 'tabular-nums' }}>
                {t('Next refresh in:')} <Box component="span" sx={{display: 'inline-block', minWidth: '2ch', textAlign: 'right'}}>{refreshCountdown}</Box>s
              </Typography>
              <IconButton
                size="small"
                onClick={onRefresh}
                disabled={vehicleOffersLoading}
                sx={{
                  p: 0.5,
                  borderRadius: '50%',
                  bgcolor: 'background.white',
                }}
              >
                <Refresh sx={{ fontSize: 16 }} />
              </IconButton>
            </>
          )}
        </Box>
      </Box>
      <TableContainer
        ref={tableContainerRef}
        sx={{
          ...styles.tableContainer,
          ...(tableContainerMaxHeight ? { maxHeight: tableContainerMaxHeight } : {}),
          scrollbarWidth: 'thin',
          msOverflowStyle: 'scrollbar',
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-thumb': {
            display: 'block',
            backgroundColor: (theme: any) => theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.15)'
              : 'rgba(0, 0, 0, 0.2)',
            borderRadius: '100px',
            transition: 'background-color 0.2s ease',
            '&:hover': {
              backgroundColor: (theme: any) => theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.28)'
                : 'rgba(0, 0, 0, 0.3)',
            },
            '&:active': {
              backgroundColor: (theme: any) => theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.36)'
                : 'rgba(0, 0, 0, 0.4)',
            },
          },
        }}
      >
        <Table stickyHeader sx={{
          ...styles.tableHead,
          width: '100%',
          height: hasNoData ? '100%' : 'auto',
          tableLayout: 'auto',
          bgcolor: hasNoData ? 'transparent' : (theme: any) => theme.palette.mode === 'dark'
            ? 'rgba(18, 18, 18, 0.95)'
            : 'rgba(255, 255, 255, 0.95)',
        }} >
          <TableHead sx={{
            ...styles.tableHead,
            bgcolor: hasNoData ? 'transparent' : (theme: any) => theme.palette.mode === 'dark'
              ? 'rgba(18, 18, 18, 0.95)'
              : 'rgba(255, 255, 255, 0.95)',
          }}>
            <TableRow>
              {cols.filter((col) => !col.hide).map((col) => (
                <TableCell key={col.id} sx={styles.tableHeaderCell}>
                  {col.sortable ? (
                    <TableSortLabel
                      active={orderBy === col.id}
                      direction={orderBy === col.id ? (order === 'ASC' ? 'asc' : 'desc') : 'desc'}
                      onClick={() => handleSortClick(col.id as PotentialOffersOrderBy)}
                      hideSortIcon={false}
                      sx={{
                        '& .MuiTableSortLabel-icon': {
                          opacity: orderBy === col.id ? 1 : 0.3,
                        },
                        '&:hover .MuiTableSortLabel-icon': {
                          opacity: 0.7,
                        },
                      }}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableSkeletonLoader
              loading={showSkeleton}
              columns={cols.filter((col) => !col.hide).length}
              rows={10}
            >
              {offers && offers.length > 0 ? (
                offers
                  .map((offer: PotentialOffer, index: number) => {

                    const isNegotiatedElsewhere = !!offer.otherNegotiatingVehicle;
                    const otherVehicleDisplay = offer.otherNegotiatingVehicle?.truckPlate || '';

                    let otherTripId: number | undefined;
                    const isInOtherGroupageTrip = offer.negotiations?.some(n => {
                      if (!n.id) return false;
                      const tripId = negotiationToGroupageTripMap?.get(n.id);
                      if (tripId !== undefined && tripId !== currentGroupageTripId) {
                        otherTripId = tripId;
                        return true;
                      }
                      return false;
                    }) ?? false;

                    const offerRenderer = new OfferRenderer(offer, theme, tenantData);

                    const rowContent = (
                      <TableRow
                        key={index}
                        sx={{
                          ...styles.tableRow,
                          opacity: isNegotiatedElsewhere ? 0.5 : 1,
                          transition: 'opacity 0.2s ease-in-out',
                        }}
                        onClick={isNegotiatedElsewhere ? undefined : () => { handleRowClick(offer); }}
                        data-active={lastClickedOfferIdRef.current === offer.id}
                      >
                        <TableCell sx={styles.tableCell}>
                          {offerRenderer.renderId()}
                        </TableCell>
                        <TableCell sx={styles.tableCell}>
                          {offerRenderer.renderRoute()}
                        </TableCell>
                        <TableCell sx={styles.tableCell}>
                          {offerRenderer.renderTimeline()}
                        </TableCell>
                        <TableCell sx={styles.tableCell}>
                          {offerRenderer.renderPrices()}
                        </TableCell>
                        <TableCell sx={styles.tableCell}>
                          {offerRenderer.renderScore()}
                        </TableCell>
                        <TableCell sx={{ ...styles.tableCell, display: { xs: 'none', sm: 'table-cell' } }}>
                          {offerRenderer.renderInfo()}
                        </TableCell>
                        <TableCell sx={styles.tableCell}>
                          {offerRenderer.renderContactPerson()}
                        </TableCell>
                        <TableCell sx={{ ...styles.tableCell, display: { xs: 'none', sm: 'table-cell' } }}>
                          {offerRenderer.renderDistance()}
                        </TableCell>
                        <TableCell sx={styles.tableCell}>
                          {offerRenderer.renderWL()}
                        </TableCell>
                        {mode === 'ongoing' && <TableCell sx={styles.tableCell}>
                          {offerRenderer.renderStatus()}
                        </TableCell>}
                        {/* age - only for potential matches */}
                        {mode === 'all' && <TableCell sx={styles.tableCell}>
                          {offerRenderer.renderAge()}
                        </TableCell>}
                        {/* actions - only for ongoing offers */}
                        {mode === 'ongoing' && <TableCell sx={{ ...styles.tableCell, minWidth: '5.5rem' }}>
                          <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                            {(() => {
                              const isRowPending = groupagePendingOfferId === offer.id;
                              const anyPending = groupagePendingOfferId !== null;
                              return <>
                                {!isNegotiatedElsewhere && !isInOtherGroupageTrip && onCreateGroupageTrip && offer.negotiations?.[0]?.id &&
                                 offer.lengthM !== undefined && tenantData?.minLengthMForFullTruckLoad !== undefined &&
                                 offer.lengthM < tenantData.minLengthMForFullTruckLoad && (
                                  <Tooltip title={t('Create groupage trip')}>
                                    <span>
                                      <IconButton size="small" color="info" disabled={anyPending} onClick={(e) => { e.stopPropagation(); onCreateGroupageTrip(offer); }}>
                                        {isRowPending ? <CircularProgress size={16} /> : <MergeTypeIcon fontSize="small" />}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                )}
                                {!isNegotiatedElsewhere && !isInOtherGroupageTrip && onAddToGroupageTrip && offer.negotiations?.[0]?.id &&
                                 !groupageTripNegotiationIds?.has(offer.negotiations[0].id) &&
                                 (groupageRemainingWeightT === undefined || (offer.weightT ?? 0) <= groupageRemainingWeightT) &&
                                 (groupageRemainingLengthM === undefined || (offer.lengthM ?? 0) <= groupageRemainingLengthM) && (
                                  <Tooltip title={t('Add to groupage trip')}>
                                    <span>
                                      <IconButton size="small" color="info" disabled={anyPending} onClick={(e) => { e.stopPropagation(); onAddToGroupageTrip(offer); }}>
                                        {isRowPending ? <CircularProgress size={16} /> : <MergeTypeIcon fontSize="small" />}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                )}
                                {isInOtherGroupageTrip && otherTripId && onOpenOtherGroupageTrip && (
                                  <Tooltip
                                    title={t('This negotiation is in groupage trip #{{tripId}}. Click to open.', {tripId: String(otherTripId)})}
                                    arrow
                                    placement="top"
                                    slotProps={{
                                      tooltip: {
                                        sx: {
                                          fontSize: (theme: any) => theme.typography.table.body,
                                          bgcolor: theme.palette.info.main,
                                          '& .MuiTooltip-arrow': {
                                            color: theme.palette.info.main,
                                          },
                                        },
                                      },
                                    }}
                                  >
                                    <span>
                                      <IconButton
                                        size="small"
                                        sx={{opacity: 0.4, '&:hover': {opacity: 0.7}}}
                                        onClick={(e) => { e.stopPropagation(); onOpenOtherGroupageTrip(otherTripId!); }}
                                      >
                                        <MergeTypeIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                )}
                              </>;
                            })()}
                            {!isNegotiatedElsewhere && (
                              <OfferActionButton
                                offer={offer}
                                vehicle={selectedTruck}
                                onRefresh={onRefresh}
                                onOrderConfirm={handleOrderConfirmNegotiation}
                                // TODO: check if _vehicle param is needed or if selectedTruck from closure is always equivalent
                                onAccept={async (_vehicle, offer) => {
                                  if (!selectedTruck || !offer.id) return;
                                  lastClickedOfferIdRef.current = offer.id;
                                  setSelectedOffer(offer);
                                  const negotiationId = offer.negotiations?.[0]?.id;
                                  if (!negotiationId) return;
                                  try {
                                    await handleFetchNegotiation(selectedTruck.id, negotiationId);
                                    setNegotiationModalOpen(true);
                                  } catch (err) {
                                    console.error('handleGetNegotiation error:', err);
                                    showErrorToast(err, t('Failed to fetch negotiation info'));
                                  }
                                }}
                                onAbort={handleAbortNegotiation}
                                onClose={handleCloseNegotiation}
                                onStart={handleOpenNegotiationModal}
                              />
                            )}
                          </Box>
                        </TableCell>}
                      </TableRow>
                    );

                    return isNegotiatedElsewhere ? (
                      <Tooltip
                        key={index}
                        title={t('This offer is currently being negotiated on {{vehicleName}}', { vehicleName: otherVehicleDisplay })}
                        arrow
                        placement="top"
                        slotProps={{
                          tooltip: {
                            sx: {
                              fontSize: (theme: any) => theme.typography.table.body,
                              bgcolor: theme.palette.text.purple,
                              '& .MuiTooltip-arrow': {
                                color: theme.palette.text.purple
                              }
                            }
                          }
                        }}
                      >
                        {rowContent}
                      </Tooltip>
                    ) : rowContent;
                  })
              ) : showAutoSearchError ? (
                <TableRow>
                  <TableCell colSpan={cols.filter((col) => !col.hide).length} sx={{ border: 'none', p: 0 }}>
                    <EmptyState
                      image="/no-data.svg"
                      title={t('Auto-search error')}
                      description={parseAutoSearchError(autoSearchInfo?.statusMessage, t)}
                    />
                  </TableCell>
                </TableRow>
              ) : showLoadError ? (
                <TableRow>
                  <TableCell colSpan={cols.filter((col) => !col.hide).length} sx={{ border: 'none', p: 0 }}>
                    <EmptyState
                      image="/no-data.svg"
                      title={t('Failed to load offers')}
                      description={t('An error occurred while fetching offers. Check your connection and try again.')}
                    />
                  </TableCell>
                </TableRow>
              ) : showFilteredOutHint ? (
                <TableRow>
                  <TableCell colSpan={cols.filter((col) => !col.hide).length} sx={{ border: 'none', p: 0 }}>
                    <EmptyState
                      image="/no-data.svg"
                      title={t('All offers filtered out')}
                      description={t('Auto-search found {{count}} raw offers on the marketplace, but none match the current filters. Try relaxing them.', { count: autoSearchInfo?.marketplaceOffersFound ?? 0 })}
                    />
                  </TableCell>
                </TableRow>
              ) : showMarketplaceEmpty ? (
                <TableRow>
                  <TableCell colSpan={cols.filter((col) => !col.hide).length} sx={{ border: 'none', p: 0 }}>
                    <EmptyState
                      image="/no-data.svg"
                      title={t('No offers on the marketplace')}
                      description={t('Auto-search did not find any offers for this route. Adjust the search criteria or wait for new postings.')}
                    />
                  </TableCell>
                </TableRow>
              ) : showGenericEmpty ? (
                <TableRow>
                  <TableCell colSpan={cols.filter((col) => !col.hide).length} sx={{ border: 'none', p: 0 }}>
                    <EmptyState
                      image="/no-data.svg"
                      title={t('No results found')}
                      description={
                        selectedTruck
                          ? t('We could not find matches for this truck. Try different filters or update manual search input.')
                          : t('No matches for current filters. Select a truck or adjust your search criteria.')
                      }
                    />
                  </TableCell>
                </TableRow>
              ) : null}
            </TableSkeletonLoader>
          </TableBody>
        </Table>

      </TableContainer>

      {/* pagination */}
      {!(totalOffers === 0 && page === 1) && totalOffers > pageSize && (
        <Stack spacing={2} alignItems="center" m={2} borderTop={1} pt={2} borderColor={'border.default'}>
          <Pagination
            count={totalOffers ? Math.ceil(totalOffers / pageSize) : 1}
            page={page}
            onChange={(_, value) => onPageChange(value)}
            color="standard"
          />
        </Stack>
      )}

      {/* Negotiation Dialog */}
      <NegotiationDialog
        open={negotiationModalOpen}
        clientPrice={selectedOffer?.fePrice}
        initialEstimatedPrice={selectedOffer?.estimatedPrice || selectedOffer?.fePrice || 0}
        initialFairPrice={
          selectedOffer
            ? (() => {
              const offer = offers?.find((booking: PotentialOffer) => booking.id === selectedOffer.id);
              return offer?.totalPrice || undefined;
            })()
            : undefined
        }
        isStarting={startNegotiation}
        isEditing={
          selectedOffer
            ? (() => {
              const offer = offers?.find((booking: PotentialOffer) => booking.id === selectedOffer.id);
              if (!offer || !offer?.negotiations?.length) return false;
              // Only allow editing when status is human action needed (status 7)
              return offer && offer.status === 7 && offer.negotiations.length > 0;
            })()
            : false
        }
        negotiation={negotiation}
        offer={selectedOffer || undefined}
        vehicle={selectedTruck}
        tenantData={tenantData}
        onRefresh={onRefresh}
        setNegotiation={setNegotiation}
        setNegotiationModalOpen={setNegotiationModalOpen}
        actions={{
          handleCloseNegotiation,
          handleOpenNegotiationModal: handleOpenNegotiationModal,
        }}
        onClose={handleCloseNegotiationModal}
        onStart={handleStart}
        onSendMessage={handleSendMessage}
        onAccept={handleAcceptNegotiation}
        onDialogClose={async () => {
          if (selectedTruck) {
            await fetchVehicles();
            onRefresh();
          }
        }}
      />

      {ConfirmModal}

      {/* Continue Negotiation Dialog */}
      <ContinueNegotiationDialog
        open={continueNegotiationModal.open}
        vehicleId={continueNegotiationModal.vehicleId}
        negotiationId={continueNegotiationModal.negotiationId!}
        messageId={continueNegotiationModal.messageId}
        messagesHistory={continueNegotiationModal.messagesHistory}
        onClose={handleCloseContinueNegotiationModal}
        onSuccess={handleContinueNegotiationSuccess}
      />

      {ConfirmNegotiationModal}
    </Box>
  );
};

export default PotentialMatchesTable;

const styles = {
  tableCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    border: '1px solid',
    borderRadius: { xs: 2, sm: 2.5, md: 3 },
    overflow: 'hidden',
    minHeight: 0,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    }
    
  },
  tableHeader: {
    p: { md: 1 },
    borderBottom: (theme: any) => theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.08)'
      : '1px solid rgba(0, 0, 0, 0.08)',
    bgcolor: 'transparent',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  tableTitle: {
    fontWeight: 500,
    fontSize: (appTheme as any).typography.fontSizes.heading2,
    color: 'text.primary',
    letterSpacing: '-0.01em',
  },
  tableContainer: {
    flex: 1,
    position: 'relative',
    overflowY: 'auto',
    overflowX: 'hidden',
    border: 'none',
    borderRadius: 0,
    minHeight: 0,
    width: '100%',
  },
  tableHead: {
    boxShadow: (theme: any) => theme.palette.mode === 'dark'
      ? '0 2px 8px rgba(0, 0, 0, 0.3)'
      : '0 2px 8px rgba(0, 0, 0, 0.1)',
    '& .MuiTableCell-root': {
      padding: { xs: '8px 4px', sm: '10px 8px', md: '12px 10px', lg: '14px 12px' },
    },
    '& .MuiTableCell-root:first-of-type': {
      paddingLeft: { xs: '12px', sm: '16px', md: '20px', lg: '24px' },
    }
  },
  tableHeaderCell: {
    fontWeight: 600,
    backgroundColor: (theme: any) => theme.palette.mode === 'dark'
      ? 'rgba(18, 18, 18, 0.98)'
      : 'rgba(255, 255, 255, 0.98)',
    padding: { xs: '12px 8px', sm: '14px 12px', md: '16px 14px', lg: '18px 16px' },
    color: (theme: any) => theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.7)'
      : 'rgba(0, 0, 0, 0.7)',
    borderBottom: (theme: any) => theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.08)'
      : '1px solid rgba(0, 0, 0, 0.08)',
    fontSize: (theme: any) => theme.typography.table.header,
    textAlign: 'center',
    letterSpacing: '0.01em',
    textTransform: 'none',
    '&:first-of-type': {
      paddingLeft: { xs: '12px', sm: '16px', md: '20px', lg: '24px' },
    },
    '& .MuiTableSortLabel-icon': {
      color: 'primary.main',
      opacity: 0.5,
      transition: 'all 0.2s ease',
      '&:hover': {
        opacity: 1,
      },
    },
    '& .MuiTableSortLabel-root': {
      '&:hover': {
        color: 'primary.main',
      },
    },
    '& .MuiTableSortLabel-root.Mui-active': {
      color: 'primary.main',
      '& .MuiTableSortLabel-icon': {
        opacity: 1,
        color: 'primary.main',
      },
    },
  },
  tableRow: {
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:nth-of-type(odd)': {
      backgroundColor: (theme: any) => theme.palette.mode === 'dark' ? 'background.sidebar' : 'background.white',
    },
    '&:nth-of-type(even)': {
      backgroundColor: (theme: any) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(248, 250, 252, 0.5)',
    },
    '&:hover': {
      backgroundColor: (theme: any) => theme.palette.mode === 'dark' ? 'rgba(102, 126, 234, 0.15)' : 'rgba(102, 126, 234, 0.08)',
      transform: 'scale(1.001)',
      boxShadow: (theme: any) => theme.palette.mode === 'dark' ? '0 2px 8px rgba(102, 126, 234, 0.25)' : '0 2px 8px rgba(102, 126, 234, 0.15)',
    },
    cursor: 'pointer',
  },
  tableCell: {
    borderBottom: 'none',
    textAlign: 'center',
    padding: { xs: '8px 8px', sm: '10px 10px', md: '12px 12px', lg: '14px 14px' },
    lineHeight: { xs: 1, sm: 1.2, md: 1.2 },
    fontSize: (theme: any) => theme.typography.table.body,
    color: (theme: any) => theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.9)'
      : 'rgba(0, 0, 0, 0.9)',
    '& .MuiTypography-root': {
      margin: 0,
      lineHeight: 'inherit',
    },
    '& .MuiBox-root': {
      margin: 0,
      lineHeight: 'inherit',
    },
  },
  emptyState: {
    textAlign: 'center',
    py: 20,
    color: 'text.grey',
    height: '100%',
    verticalAlign: 'middle',
    fontSize: (theme: any) => theme.typography.fontSizes.body,
    fontWeight: 500,
  }
};
